"use client";

import logger from "@/lib/logger";
import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	Download,
	Loader,
	Music,
	Pause,
	Play,
	Settings,
	Upload,
	Video,
	Gauge,
	HardDrive,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import VideoPlayer from "@/components/VideoPlayer";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { useAccessibility } from "@/hooks/useAccessibility";
import { useTranslations } from "next-intl";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FileDropZone } from "@/components/FileDropZone";
import type WaveSurfer from "wavesurfer.js";

// Define compression presets with file type detection
const COMPRESSION_PRESETS = {
	video: [
		{
			labelKey: "presets.video.light.label",
			ext: "mp4",
			mime: "video/mp4",
			icon: Video,
			command: ["-c:v", "libx264", "-crf", "20", "-preset", "medium", "-c:a", "aac", "-b:a", "192k"],
			descriptionKey: "presets.video.light.description"
		},
		{
			labelKey: "presets.video.medium.label",
			ext: "mp4",
			mime: "video/mp4",
			icon: Video,
			command: ["-c:v", "libx264", "-crf", "25", "-preset", "medium", "-c:a", "aac", "-b:a", "128k"],
			descriptionKey: "presets.video.medium.description"
		},
		{
			labelKey: "presets.video.high.label",
			ext: "mp4",
			mime: "video/mp4",
			icon: Video,
			command: ["-c:v", "libx264", "-crf", "30", "-preset", "fast", "-c:a", "aac", "-b:a", "96k"],
			descriptionKey: "presets.video.high.description"
		},
		{
			labelKey: "presets.video.maximum.label",
			ext: "mp4",
			mime: "video/mp4",
			icon: Video,
			command: ["-c:v", "libx264", "-crf", "35", "-preset", "fast", "-c:a", "aac", "-b:a", "64k"],
			descriptionKey: "presets.video.maximum.description"
		},
		{
			labelKey: "presets.video.hevc.label",
			ext: "mp4",
			mime: "video/mp4",
			icon: Video,
			command: ["-c:v", "libx265", "-crf", "28", "-preset", "medium", "-c:a", "aac", "-b:a", "128k"],
			descriptionKey: "presets.video.hevc.description"
		},
		{
			labelKey: "presets.video.vp9.label",
			ext: "webm",
			mime: "video/webm",
			icon: Video,
			command: ["-c:v", "libvpx-vp9", "-crf", "30", "-b:v", "0", "-c:a", "libopus", "-b:a", "128k"],
			descriptionKey: "presets.video.vp9.description"
		}
	],
	audio: [
		{
			labelKey: "presets.audio.light.label",
			ext: "aac",
			mime: "audio/aac",
			icon: Music,
			command: ["-vn", "-c:a", "aac", "-b:a", "192k"],
			descriptionKey: "presets.audio.light.description"
		},
		{
			labelKey: "presets.audio.medium.label",
			ext: "aac",
			mime: "audio/aac",
			icon: Music,
			command: ["-vn", "-c:a", "aac", "-b:a", "128k"],
			descriptionKey: "presets.audio.medium.description"
		},
		{
			labelKey: "presets.audio.high.label",
			ext: "mp3",
			mime: "audio/mp3",
			icon: Music,
			command: ["-vn", "-c:a", "libmp3lame", "-b:a", "96k"],
			descriptionKey: "presets.audio.high.description"
		},
		{
			labelKey: "presets.audio.maximum.label",
			ext: "mp3",
			mime: "audio/mp3",
			icon: Music,
			command: ["-vn", "-c:a", "libmp3lame", "-b:a", "64k"],
			descriptionKey: "presets.audio.maximum.description"
		},
		{
			labelKey: "presets.audio.opus.label",
			ext: "opus",
			mime: "audio/opus",
			icon: Music,
			command: ["-vn", "-c:a", "libopus", "-b:a", "128k"],
			descriptionKey: "presets.audio.opus.description"
		}
	]
};

interface CompressionSettings {
	videoCodec: string;
	audioCodec: string;
	videoBitrate: number;
	audioBitrate: number;
	crf: number;
	preset: string;
	resolution: string;
	enableTwoPass: boolean;
	customSettings: string;
}

const DEFAULT_COMPRESSION_SETTINGS: CompressionSettings = {
	videoCodec: "libx264",
	audioCodec: "aac", 
	videoBitrate: 1000,
	audioBitrate: 128,
	crf: 23,
	preset: "medium",
	resolution: "original",
	enableTwoPass: false,
	customSettings: ""
};

const VideoCompressor = () => {
	const t = useTranslations("videoCompressor");
	const { ffmpegInstance, isLoaded, isLoading, hardwareInfo, error, loadFFmpeg } = useFFmpeg();

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const { announce, announceProgress, announceError, announceSuccess, focusElement } = useAccessibility();
	
	const [inputFile, setInputFile] = useState<File | null>(null);
	const [fileType, setFileType] = useState<'video' | 'audio' | null>(null);
	const [selectedPreset, setSelectedPreset] = useState(COMPRESSION_PRESETS.video[1]); // Default to medium compression
	const [outputURL, setOutputURL] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [statusLog, setStatusLog] = useState(t("status.initializing"));
	const [progress, setProgress] = useState(0);
	const [startTime, setStartTime] = useState<number | null>(null);
	const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string>("");
	const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
	const [compressionSettings, setCompressionSettings] = useState<CompressionSettings>(DEFAULT_COMPRESSION_SETTINGS);
	const [originalFileSize, setOriginalFileSize] = useState<number>(0);
	const [compressedFileSize, setCompressedFileSize] = useState<number>(0);
	const waveformRef = useRef<HTMLDivElement>(null);
	const wavesurferRef = useRef<WaveSurfer | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [isClient, setIsClient] = useState(false);

	// Track client-side mounting
	useEffect(() => {
		setIsClient(true);
	}, []);

	// File type detection
	const detectFileType = useCallback((file: File): 'video' | 'audio' => {
		const mimeType = file.type;
		if (mimeType.startsWith('video/')) {
			return 'video';
		} else if (mimeType.startsWith('audio/')) {
			return 'audio';
		}
		
		// Fallback to file extension
		const extension = file.name.split('.').pop()?.toLowerCase() || '';
		const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv', '3gp', 'mpeg', 'ts', 'm4v', 'vob'];
		const audioExtensions = ['mp3', 'aac', 'wav', 'ogg', 'flac', 'm4a', 'wma'];
		
		if (videoExtensions.includes(extension)) {
			return 'video';
		} else if (audioExtensions.includes(extension)) {
			return 'audio';
		}
		
		return 'video'; // Default to video
	}, []);

	// Helper function to calculate estimated time remaining
	const calculateTimeRemaining = useCallback((currentProgress: number, startTime: number): string => {
		if (currentProgress <= 0) return "";

		const elapsed = Date.now() - startTime;
		const totalEstimated = elapsed / currentProgress;
		const remaining = totalEstimated - elapsed;

		if (remaining <= 0) return t("timeRemaining.almostDone");

		const minutes = Math.floor(remaining / 60000);
		const seconds = Math.floor((remaining % 60000) / 1000);

		if (minutes > 0) {
			return t("timeRemaining.minutesSeconds", { minutes, seconds });
		}
		return t("timeRemaining.seconds", { seconds });
	}, [t]);

	// Build compression command based on settings
	const buildCompressionCommand = useCallback((preset: any, settings: CompressionSettings, isAdvanced: boolean): string[] => {
		if (!isAdvanced) {
			return preset.command;
		}

		const command: string[] = [];
		
		// Video codec and settings
		if (fileType === 'video') {
			command.push("-c:v", settings.videoCodec);
			
			if (settings.videoCodec.includes('x264') || settings.videoCodec.includes('x265')) {
				command.push("-crf", settings.crf.toString());
				command.push("-preset", settings.preset);
			} else {
				command.push("-b:v", `${settings.videoBitrate}k`);
			}
			
			// Resolution scaling
			if (settings.resolution !== "original") {
				command.push("-vf", `scale=${settings.resolution}`);
			}
		}
		
		// Audio codec and settings 
		command.push("-c:a", settings.audioCodec);
		command.push("-b:a", `${settings.audioBitrate}k`);
		
		// Custom settings
		if (settings.customSettings.trim()) {
			const customArgs = settings.customSettings.trim().split(/\s+/);
			command.push(...customArgs);
		}
		
		return command;
	}, [fileType]);

	// Initialize wavesurfer for audio files
	const initializeWaveSurfer = useCallback(async (audioUrl: string) => {
		if (!isClient || !waveformRef.current || fileType !== 'audio') return;

		try {
			// Clean up existing wavesurfer instance
			if (wavesurferRef.current) {
				wavesurferRef.current.destroy();
				wavesurferRef.current = null;
			}

			// Dynamically import WaveSurfer to reduce initial bundle size
			const WaveSurferModule = await import("wavesurfer.js");
			const WaveSurfer = WaveSurferModule.default;

			// Create new wavesurfer instance
			wavesurferRef.current = WaveSurfer.create({
				container: waveformRef.current,
				waveColor: '#3b82f6',
				progressColor: '#1d4ed8',
				cursorColor: '#ef4444',
				barWidth: 2,
				barRadius: 3,
				height: 120,
				normalize: true,
				fillParent: true,
				mediaControls: false,
				interact: true
			});

			// Set up event listeners
			wavesurferRef.current.on('audioprocess', (time) => {
				setCurrentTime(time);
			});

			wavesurferRef.current.on('play', () => {
				setIsPlaying(true);
			});

			wavesurferRef.current.on('pause', () => {
				setIsPlaying(false);
			});

			wavesurferRef.current.on('seeking', (progress: number) => {
				if (wavesurferRef.current && progress !== undefined) {
					const time = progress * wavesurferRef.current.getDuration();
					setCurrentTime(time);
				}
			});

			wavesurferRef.current.on('error', (error) => {
				logger.error({ err: error }, "WaveSurfer error");
			});

			// Load audio file after setting up listeners
			wavesurferRef.current.load(audioUrl);
		} catch (error) {
			logger.error({ err: error }, "Failed to initialize WaveSurfer");
		}
	}, [isClient, fileType]);

	// Audio playback controls
	const togglePlayPause = useCallback(() => {
		if (wavesurferRef.current) {
			if (isPlaying) {
				wavesurferRef.current.pause();
			} else {
				wavesurferRef.current.play().catch((err) => logger.error({ err }, "WaveSurfer play error"));
			}
		}
	}, [isPlaying]);

	// Format time helper
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		const ms = Math.floor((seconds % 1) * 100);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
	};

	// Initialize WaveSurfer when compressed audio output is available
	useEffect(() => {
		if (outputURL && fileType === 'audio' && isClient) {
			// Small delay to ensure DOM is ready
			const timer = setTimeout(() => {
				initializeWaveSurfer(outputURL);
			}, 100);
			
			return () => clearTimeout(timer);
		}
	}, [outputURL, fileType, isClient, initializeWaveSurfer]);

	// Cleanup wavesurfer on unmount
	useEffect(() => {
		return () => {
			if (wavesurferRef.current) {
				wavesurferRef.current.destroy();
			}
		};
	}, []);

	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			setStatusLog(t("status.ready"));
		} else {
			setStatusLog(t("status.initializing"));
		}
	}, [error, isLoading, isLoaded, t]);

	// Set up FFmpeg progress listener
	useEffect(() => {
		if (ffmpegInstance && isProcessing) {
			const handleProgress = ({ progress }: { progress: number }) => {
				setProgress(progress);
				if (startTime) {
					setEstimatedTimeRemaining(calculateTimeRemaining(progress, startTime));
				}
				announceProgress(Math.round(progress * 100), 100, t("a11y.progressDescription"));
			};

			ffmpegInstance.on("progress", handleProgress);
			return () => {
				ffmpegInstance.off("progress", handleProgress);
			};
		}
	}, [ffmpegInstance, isProcessing, startTime, calculateTimeRemaining, announceProgress]);

	// Handle file selection
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const detectedType = detectFileType(file);
			setInputFile(file);
			setFileType(detectedType);
			setOriginalFileSize(file.size);
			setOutputURL("");
			setCompressedFileSize(0);
			
			// Set appropriate preset based on file type
			const presets = COMPRESSION_PRESETS[detectedType];
			setSelectedPreset(presets[1]); // Default to medium compression
			
			setStatusLog(t("status.fileSelected", { type: detectedType === 'video' ? 'Video' : 'Audio', name: file.name }));
			announce(t("a11y.fileSelected", { type: detectedType === 'video' ? 'Video' : 'Audio', name: file.name, size: (file.size / 1024 / 1024).toFixed(1) }));
			
			// Clean up any existing wavesurfer when new file is selected
			if (wavesurferRef.current) {
				wavesurferRef.current.destroy();
				wavesurferRef.current = null;
			}
		}
	};

	// Core compression logic
	const compressFile = useCallback(async () => {
		if (!inputFile || !ffmpegInstance || !fileType) return;

		setIsProcessing(true);
		setOutputURL("");
		setProgress(0);
		setStartTime(Date.now());
		setEstimatedTimeRemaining("");
		setStatusLog(t("status.compressing", { name: inputFile.name }));

		announce(t("a11y.compressionStarted"), "polite");

		const inputFileName = `input.${inputFile.name.split(".").pop()}`;
		const outputFileName = `compressed.${selectedPreset.ext}`;

		try {
			// Check file size limits for mobile devices
			const fileSizeMB = inputFile.size / (1024 * 1024);
			const memoryLimitMB = hardwareInfo.memory * 1024;

			if (hardwareInfo.isLowEnd && fileSizeMB > memoryLimitMB * 0.3) {
				throw new Error(t("errors.fileTooLarge", { size: fileSizeMB.toFixed(1) }));
			}

			setStatusLog(t("status.loadingFile"));

			// Write file to FFmpeg filesystem
			await ffmpegInstance.writeFile(inputFileName, await fetchFile(inputFile));

			// Build compression command
			const compressionCommand = buildCompressionCommand(selectedPreset, compressionSettings, showAdvancedSettings);
			
			// Optimize for hardware capabilities
			let threadCount = hardwareInfo.cores;
			if (hardwareInfo.isLowEnd) {
				threadCount = 1;
			} else if (!hardwareInfo.supportsSharedArrayBuffer) {
				threadCount = Math.max(1, Math.floor(threadCount * 0.5));
			}

			const fullCommand = [
				"-i", inputFileName,
				...compressionCommand,
				"-threads", threadCount.toString(),
				outputFileName
			];

			setStatusLog(t("status.runningCompression", { threads: threadCount }));

			// Debug command
			logger.debug({ command: fullCommand }, "FFmpeg Compression Command");
			logger.debug({ preset: selectedPreset.labelKey }, "Selected Preset");
			logger.debug({ fileType }, "File Type");

			await ffmpegInstance.exec(fullCommand);

			setStatusLog(t("status.readingFile"));

			// Read output file
			const data = await ffmpegInstance.readFile(outputFileName) as Uint8Array;
			
			// Create downloadable blob
			const blob = new Blob([data as BlobPart], { type: selectedPreset.mime });
			const url = URL.createObjectURL(blob);
			
			setOutputURL(url);
			setCompressedFileSize(blob.size);
			setProgress(1);
			setEstimatedTimeRemaining("");

			const compressionRatio = ((originalFileSize - blob.size) / originalFileSize) * 100;
			setStatusLog(t("status.compressionComplete", { ratio: compressionRatio.toFixed(1) }));

			announceSuccess(t("a11y.compressionComplete", { ratio: compressionRatio.toFixed(1) }));
			setTimeout(() => {
				focusElement("[data-download-button]", 500);
			}, 1000);

		} catch (error) {
			let errorMessage = t("errors.unknown");
			
			if (error instanceof Error) {
				errorMessage = error.message;
			} else if (typeof error === 'string') {
				errorMessage = error;
			}
			
			setStatusLog(t("status.compressionFailed", { error: errorMessage }));
			setProgress(0);
			setEstimatedTimeRemaining("");
			announceError(errorMessage);

		} finally {
			setIsProcessing(false);
			// Cleanup filesystem
			try {
				await ffmpegInstance.deleteFile(inputFileName);
				await ffmpegInstance.deleteFile(outputFileName);
			} catch {
				// Ignore cleanup errors
			}
		}
	}, [
		inputFile,
		ffmpegInstance,
		fileType,
		selectedPreset,
		compressionSettings,
		showAdvancedSettings,
		hardwareInfo,
		originalFileSize,
		buildCompressionCommand,
		announce,
		announceSuccess,
		announceError,
		focusElement,
		t,
	]);

	const FormatIcon = selectedPreset.icon;

	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<HardDrive className="h-8 w-8 me-4 text-destructive" aria-hidden="true" />
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>

					{/* Status Bar */}
					<Alert className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}>
						<AlertTriangle className="h-4 w-4" aria-hidden="true" />
						<AlertDescription className="font-bold">{statusLog}</AlertDescription>
					</Alert>

					{/* Main Interface */}
					<div className="flex flex-col gap-6">
						{/* 1. Upload Section */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Upload className="w-6 h-6" aria-hidden="true" />
									{t("steps.selectFile")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<FileDropZone
									accept="video/*,audio/*"
									disabled={!isLoaded || isProcessing}
									onFiles={(files) => handleFileChange({ target: { files } } as any)}
									label={t("fileInput.dropLabel")}
									activeLabel={t("fileInput.dropActiveLabel")}
								/>
								{inputFile && (
									<div className="mt-3 space-y-2">
										<p className="text-sm font-bold">
											<span className="text-primary">{inputFile.name}</span> 
											<Badge variant="secondary" className="ms-2">{fileType?.toUpperCase()}</Badge>
										</p>
										<p className="text-sm text-muted-foreground">
											{t("fileInput.originalSize", { size: (originalFileSize / 1024 / 1024).toFixed(2) })}
										</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* 2. Compression Preset Selection */}
						{fileType && (
							<Card className="p-3 sm:p-6">
								<CardHeader className="p-0 mb-4">
									<CardTitle className="flex items-center gap-3">
										<FormatIcon className="w-6 h-6" aria-hidden="true" />
										{t("steps.selectLevel")}
									</CardTitle>
								</CardHeader>
								<CardContent className="p-0">
									<Select
										value={COMPRESSION_PRESETS[fileType].indexOf(selectedPreset).toString()}
										onValueChange={(value) => {
											const presetIndex = parseInt(value);
											const preset = COMPRESSION_PRESETS[fileType][presetIndex];
											if (preset) {
												setSelectedPreset(preset);
												setOutputURL("");
												setStatusLog(t("status.presetChanged", { label: t(preset.labelKey) }));
											}
										}}
										disabled={!isLoaded || isProcessing}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder={t("presets.selectPlaceholder")} />
										</SelectTrigger>
										<SelectContent>
											{COMPRESSION_PRESETS[fileType].map((preset, idx) => (
												<SelectItem key={idx} value={idx.toString()}>
													<div className="flex flex-col">
														<span>{t(preset.labelKey)}</span>
														<span className="text-xs text-muted-foreground">{t(preset.descriptionKey)}</span>
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="mt-2 text-sm text-muted-foreground font-bold">
										{t("presets.selected", { label: t(selectedPreset.labelKey) })}
									</p>
								</CardContent>
							</Card>
						)}

						{/* 3. Advanced Settings */}
						{fileType && (
							<Card className="p-3 sm:p-6">
								<CardHeader className="p-0 mb-4">
									<CardTitle className="flex items-center gap-3">
										<Settings className="w-6 h-6" aria-hidden="true" />
										{t("steps.advancedSettings")}
									</CardTitle>
								</CardHeader>
								<CardContent className="p-0">
									<div className="mb-4">
										<Button
											variant="outline"
											onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
											className="w-full"
											aria-expanded={showAdvancedSettings}
										>
											<Gauge className="w-4 h-4 me-2" aria-hidden="true" />
											{showAdvancedSettings ? t("advanced.hideSettings") : t("advanced.showSettings")}
										</Button>
									</div>

									{showAdvancedSettings && (
										<div className="space-y-6">
											{fileType === 'video' && (
												<>
													{/* Video Codec */}
													<div>
														<Label className="text-sm font-medium">{t("advanced.videoCodec")}</Label>
														<Select
															value={compressionSettings.videoCodec}
															onValueChange={(value) =>
																setCompressionSettings(prev => ({ ...prev, videoCodec: value }))
															}
														>
															<SelectTrigger className="mt-1">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="libx264">{t("advanced.videoCodecH264")}</SelectItem>
																<SelectItem value="libx265">{t("advanced.videoCodecH265")}</SelectItem>
																<SelectItem value="libvpx-vp9">{t("advanced.videoCodecVP9")}</SelectItem>
															</SelectContent>
														</Select>
													</div>

													{/* CRF Quality */}
													{(compressionSettings.videoCodec.includes('x264') || compressionSettings.videoCodec.includes('x265')) && (
														<div>
															<Label className="text-sm font-medium">
																{t("advanced.qualityCrf", { value: compressionSettings.crf })}
																<span className="text-xs text-muted-foreground ms-2">
																	{t("advanced.qualityCrfHint")}
																</span>
															</Label>
															<Slider
																value={[compressionSettings.crf]}
																onValueChange={([value]) =>
																	setCompressionSettings(prev => ({ ...prev, crf: value }))
																}
																min={18}
																max={35}
																step={1}
																className="mt-1"
															/>
														</div>
													)}

													{/* Video Bitrate */}
													{!compressionSettings.videoCodec.includes('x264') && !compressionSettings.videoCodec.includes('x265') && (
														<div>
															<Label className="text-sm font-medium">{t("advanced.videoBitrate", { value: compressionSettings.videoBitrate })}</Label>
															<Slider
																value={[compressionSettings.videoBitrate]}
																onValueChange={([value]) =>
																	setCompressionSettings(prev => ({ ...prev, videoBitrate: value }))
																}
																min={500}
																max={5000}
																step={100}
																className="mt-1"
															/>
														</div>
													)}

													{/* Preset Speed */}
													<div>
														<Label className="text-sm font-medium">{t("advanced.encodingSpeed")}</Label>
														<Select
															value={compressionSettings.preset}
															onValueChange={(value) =>
																setCompressionSettings(prev => ({ ...prev, preset: value }))
															}
														>
															<SelectTrigger className="mt-1">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="ultrafast">{t("advanced.speedUltrafast")}</SelectItem>
																<SelectItem value="superfast">{t("advanced.speedSuperfast")}</SelectItem>
																<SelectItem value="veryfast">{t("advanced.speedVeryfast")}</SelectItem>
																<SelectItem value="faster">{t("advanced.speedFaster")}</SelectItem>
																<SelectItem value="fast">{t("advanced.speedFast")}</SelectItem>
																<SelectItem value="medium">{t("advanced.speedMedium")}</SelectItem>
																<SelectItem value="slow">{t("advanced.speedSlow")}</SelectItem>
																<SelectItem value="slower">{t("advanced.speedSlower")}</SelectItem>
																<SelectItem value="veryslow">{t("advanced.speedVeryslow")}</SelectItem>
															</SelectContent>
														</Select>
													</div>

													{/* Resolution */}
													<div>
														<Label className="text-sm font-medium">{t("advanced.outputResolution")}</Label>
														<Select
															value={compressionSettings.resolution}
															onValueChange={(value) =>
																setCompressionSettings(prev => ({ ...prev, resolution: value }))
															}
														>
															<SelectTrigger className="mt-1">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="original">{t("advanced.resOriginal")}</SelectItem>
																<SelectItem value="854x480">{t("advanced.res480")}</SelectItem>
																<SelectItem value="1280x720">{t("advanced.res720")}</SelectItem>
																<SelectItem value="1920x1080">{t("advanced.res1080")}</SelectItem>
															</SelectContent>
														</Select>
													</div>
												</>
											)}

											{/* Audio Codec */}
											<div>
												<Label className="text-sm font-medium">{t("advanced.audioCodec")}</Label>
												<Select
													value={compressionSettings.audioCodec}
													onValueChange={(value) =>
														setCompressionSettings(prev => ({ ...prev, audioCodec: value }))
													}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="aac">{t("advanced.audioCodecAAC")}</SelectItem>
														<SelectItem value="libmp3lame">{t("advanced.audioCodecMP3")}</SelectItem>
														<SelectItem value="libopus">{t("advanced.audioCodecOpus")}</SelectItem>
														<SelectItem value="copy">{t("advanced.audioCodecCopy")}</SelectItem>
													</SelectContent>
												</Select>
											</div>

											{/* Audio Bitrate */}
											{compressionSettings.audioCodec !== 'copy' && (
												<div>
													<Label className="text-sm font-medium">{t("advanced.audioBitrate", { value: compressionSettings.audioBitrate })}</Label>
													<Slider
														value={[compressionSettings.audioBitrate]}
														onValueChange={([value]) =>
															setCompressionSettings(prev => ({ ...prev, audioBitrate: value }))
														}
														min={64}
														max={320}
														step={32}
														className="mt-1"
													/>
												</div>
											)}

											{/* Custom Settings */}
											<div>
												<Label className="text-sm font-medium" htmlFor="custom-settings">{t("advanced.customArguments")}</Label>
												<input
													id="custom-settings"
													type="text"
													placeholder="-movflags +faststart"
													value={compressionSettings.customSettings}
													onChange={(e) =>
														setCompressionSettings(prev => ({ ...prev, customSettings: e.target.value }))
													}
													className="mt-1 w-full px-3 py-2 border border-input rounded-md text-sm"
												/>
												<p className="text-xs text-muted-foreground mt-1">
													{t("advanced.customHint")}
												</p>
											</div>

											<Alert>
												<AlertTriangle className="h-4 w-4" aria-hidden="true" />
												<AlertDescription className="text-xs">
													{t("advanced.warning")}
												</AlertDescription>
											</Alert>
										</div>
									)}
								</CardContent>
							</Card>
						)}

						{/* 4. Process Button */}
						<div className="flex justify-center">
							<Button
								onClick={compressFile}
								size="lg"
								variant={isLoaded && inputFile && !isProcessing ? "default" : "outline"}
								disabled={!isLoaded || !inputFile || isProcessing}
								className="px-8 py-4 text-lg"
							>
								{isProcessing ? (
									<>
										<Loader className="w-5 h-5 me-3 animate-spin" aria-hidden="true" />
										{t("convert.compressing")}
									</>
								) : (
									<>
										<Play className="w-5 h-5 me-3" aria-hidden="true" />
										{t("convert.startCompression")}
									</>
								)}
							</Button>
						</div>

						{/* Progress Bar */}
						{isProcessing && (
							<Card className="mb-6 p-3 sm:p-6">
								<div className="flex justify-between items-center mb-4">
									<Badge variant="secondary" className="font-black text-lg">
										{t("progress.compressingFile", { type: fileType?.toUpperCase() ?? "" })}
									</Badge>
									<span className="text-lg font-black">{Math.round(progress * 100)}%</span>
								</div>
								<Progress
									value={progress * 100}
									className="mb-4"
									aria-label={t("progress.progressLabel", { percent: Math.round(progress * 100) })}
								/>
								{estimatedTimeRemaining && (
									<div className="text-sm font-bold text-center text-muted-foreground">
										{estimatedTimeRemaining}
									</div>
								)}
							</Card>
						)}

						{/* 5. Download Section */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Download className="w-6 h-6" aria-hidden="true" />
									{t("steps.downloadFile")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								{outputURL ? (
									<div className="space-y-6">
										{/* Compression Stats */}
										<div className="p-4">
											<h4 className="font-semibold mb-3">{t("results.title")}</h4>
											<div className="grid grid-cols-2 gap-4 text-sm">
												<div>
													<span className="text-muted-foreground">{t("results.originalSize")}</span>
													<div className="font-bold">{(originalFileSize / 1024 / 1024).toFixed(2)} MB</div>
												</div>
												<div>
													<span className="text-muted-foreground">{t("results.compressedSize")}</span>
													<div className="font-bold">{(compressedFileSize / 1024 / 1024).toFixed(2)} MB</div>
												</div>
												<div>
													<span className="text-muted-foreground">{t("results.sizeReduction")}</span>
													<div className="font-bold text-green-600">
														{((originalFileSize - compressedFileSize) / originalFileSize * 100).toFixed(1)}%
													</div>
												</div>
												<div>
													<span className="text-muted-foreground">{t("results.spaceSaved")}</span>
													<div className="font-bold text-green-600">
														{((originalFileSize - compressedFileSize) / 1024 / 1024).toFixed(2)} MB
													</div>
												</div>
											</div>
										</div>

										{/* Audio Waveform Visualization */}
										{fileType === 'audio' && outputURL && (
											<div>
												<h4 className="text-md font-bold mb-3">{t("preview.audioWaveform")}</h4>
												<div className="w-full h-32 bg-muted rounded-lg border-2 border-muted-foreground/20 overflow-hidden">
													{isClient ? (
														<div 
															ref={waveformRef}
															className="w-full h-full"
														/>
													) : (
														<div className="w-full h-full flex items-center justify-center text-muted-foreground">
															{t("preview.loadingWaveform")}
														</div>
													)}
												</div>
												
												{/* Audio Controls */}
												<div className="flex items-center justify-center gap-4 mt-4">
													<Button
														onClick={togglePlayPause}
														variant="outline"
														size="lg"
														disabled={!wavesurferRef.current}
														aria-label={isPlaying ? t("a11y.pause") : t("a11y.play")}
													>
														{isPlaying ? (
															<Pause className="w-5 h-5" />
														) : (
															<Play className="w-5 h-5" />
														)}
													</Button>
													<span className="text-sm font-mono">
														{formatTime(currentTime)}
													</span>
												</div>
											</div>
										)}

										{/* Video Preview for video files */}
										{fileType === 'video' && (
											<div>
												<h4 className="text-md font-bold mb-3">{t("preview.videoPreview")}</h4>
												<VideoPlayer
													videoUrl={outputURL}
													fileName={`compressed.${selectedPreset.ext}`}
												/>
											</div>
										)}

										{/* Download Button */}
										<div className="flex flex-col items-center justify-center text-center space-y-4 pt-4 border-t">
											<Badge variant="secondary" className="text-lg font-black px-4 py-2">
												{t("download.completed")}
											</Badge>
											<Button asChild size="lg" className="px-6 py-3">
												<a
													href={outputURL}
													download={`compressed.${selectedPreset.ext}`}
													data-download-button
													aria-label={t("download.downloadLabel", { format: selectedPreset.ext.toUpperCase() })}
													onClick={() => announce(t("a11y.downloadStarted", { ext: selectedPreset.ext }))}
												>
													<Download className="w-5 h-5 me-3" aria-hidden="true" />
													{t("download.downloadFile", { ext: selectedPreset.ext })}
												</a>
											</Button>
										</div>
									</div>
								) : (
									<div className="text-center min-h-[12rem] flex items-center justify-center">
										{isProcessing ? (
											<p className="text-primary font-bold text-lg">
												{t("download.processingMessage")}
											</p>
										) : (
											<p className="text-muted-foreground font-bold">
												{t("download.emptyMessage")}
											</p>
										)}
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Information */}
					<Alert className="mt-8">
						<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{t("privacy")}
						</AlertDescription>
					</Alert>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default VideoCompressor;